import React, { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  createColumnHelper, FilterFn, flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel,
  Row, SortingState, useReactTable
} from '@tanstack/react-table';

import DebouncedInput from '../../components/Library/DebouncedInput';
import { getIconForSortDirection } from '../../components/Library/getIconForSortDirection';
import CenterLoadingSpinner from '../../components/LoadingSpinner/CenterLoadingSpinner';
import { useAccountContext } from '../../contexts/AccountContext';
import { useAPI } from '../../libs/libAPI';
import { exportToCsv } from '../../libs/libCSV';
import { formatToLocalTime } from '../../libs/libDate';
import { useQueryObjectState, useQueryState } from '../../libs/useQueryState';
import { sortBy } from '../../libs/utils';
import WDAccount, { WDAccountSchema } from '../../types/WDAccount';
import { ISO1806_TIMESTAMP_REGEX } from '../Systems/getDisplayValue';

const filter: FilterFn<any> = (row: Row<any>, columnId: string, filterValue: any): boolean => {
  const value = row.getValue(columnId);
  const filter = filterValue.toLowerCase();
  if (typeof value === 'number') {
    return value.toString().includes(filter);
  }
  if (typeof value === 'boolean') {
    return (value ? 'true' : 'false').includes(filter);
  }
  if (typeof value === 'string') {
    if (ISO1806_TIMESTAMP_REGEX.test(value)) {
      return formatToLocalTime(value).toLowerCase().includes(filter);
    }
    return value.toLowerCase().includes(filter);
  }
  return false;
};

const getString = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    return value;
  }
};

const columnHelper = createColumnHelper<WDAccount>();

const PartnerAccounts: React.FC = () => {
  const api = useAPI();
  const accountContext = useAccountContext();

  const [sorting, setSorting] = useQueryObjectState<SortingState>('sort', [{ desc: false, id: 'name' }]);
  const [globalFilter, setGlobalFilter] = useQueryState('q', '');

  const accountsQuery = useQuery(['partner-accounts', accountContext.accountId], async () => {
    const response = await api.fetch(`${import.meta.env.VITE_API_URI}/v1/accounts`, {
      method: 'GET'
    });

    const accounts = await WDAccountSchema.array().parseAsync(await response.json());

    return accounts.sort(sortBy('name'));
  });

  const selectAccount = (accountId: string) => {
    return () => {
      accountContext.setAccountId(accountId);
    };
  };

  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: () => 'Account Name',
      cell: info => <a onClick={selectAccount(info.row.original.id)} className='cursor-pointer underline'>{info.getValue()}</a>
    }),
    columnHelper.accessor('entitlements', {
      header: () => 'Entitlements',
      cell: info => info.getValue()?.sort().join(', ') ?? <i>None</i>
    })
  ], []);

  const exportData = (): void => {
    exportToCsv(`${accountContext.currentAccount?.name ?? 'unknown'}-accounts-list.csv`, accountsQuery.data?.map((a) => ({
      id: a.id,
      name: a.name,
      entitlements: a.entitlements?.sort()
    })) ?? []);
  };

  const table = useReactTable({
    columns,
    data: accountsQuery.data ?? [],
    state: {
      sorting,
      globalFilter
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    globalFilterFn: filter
  });

  return <>
    <div className='px-4'>
      <div className='flex mx-12'>
        <h2 className='text-2xl w-'>
          Accounts List
        </h2>
        <div className='md:ml-auto flex'>
          <button className='whitespace-nowrap mr-3 px-2 rounded hover:bg-hover' onClick={exportData}><i className='bi bi-download' /> Export</button>
          {/* <ColumnVisibility columnDisplayNames={props.columns} currentVisibility={visibleColumns} setVisibility={setVisibleColumns} defaultVisibility={defaultVisibleColumns} /> */}
          <DebouncedInput value={globalFilter} onChange={setGlobalFilter} debounce={500} placeholder='Filter Data' className='inline-block' />
        </div>
      </div>
      <div>
        {accountsQuery.data == null
          ? <CenterLoadingSpinner />
          : <>
          <table className='w-full max-w-full md:w-[calc(100vw-416px)] mx-8 md:mx-12 overflow-hidden table-fixed'>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className='border-b border-b-background3-dark'>
                      {headerGroup.headers.map((header) => (
                        <th key={header.id} className={`py-3 text-left px-2 w-col-${header.column.id} ${header.column.id === 'subject' ? 'table-cell' : 'hidden md:table-cell'}`}>
                          {header.isPlaceholder
                            ? null
                            : <div onClick={header.column.getToggleSortingHandler()} className={`flex ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}>
                              <div className='inline-block'>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                              <div className='inline-block ml-auto'>{getIconForSortDirection(header.column.getIsSorted())}</div>
                            </div>}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map(row => (
                    <tr key={row.id} className='border-b border-b-background3-dark hover:bg-hover'>
                      {row.getVisibleCells().map(cell => {
                        const overflowClasses = 'overflow-hidden overflow-ellipsis';
                        const useOverflow = cell.column.id === 'subject';
                        return (
                          <td key={cell.id} className={`p-2 whitespace-nowrap w-col-${cell.column.id} ${overflowClasses} ${useOverflow ? 'table-cell' : 'hidden md:table-cell'}`} title={getString(cell.getValue())}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {table.getRowModel().rows.length === 0 && <>
                    <tr className='border-b border-b-background3-dark'>
                      <td colSpan={columns.length} className='p-2'>
                        There are no accounts to show.
                      </td>
                    </tr>
                  </>}
                </tbody>
              </table>
        </>}
      </div>
    </div>
  </>;
};

export default PartnerAccounts;
